/**
 * Accessible on/off switch (controlled).
 *
 * Public interface: a switch driven by `checked` + `onChange`. Hides the ARIA
 * switch wiring (role="switch", aria-checked), the track/thumb markup, and the
 * on/off state styling. Keyboard activation (Space/Enter) is handled natively
 * by the underlying <button>.
 *
 * Props:
 *   checked   — boolean on/off state (controlled)
 *   onChange  — (next: boolean) => void
 *   label     — accessible label text (also rendered beside the switch)
 *   id        — optional id for the control
 *   disabled  — disables interaction
 *   className — extra class(es) on the wrapper
 *
 * All other props pass through to the <button>.
 */
export default function Toggle({
  checked = false,
  onChange,
  label,
  id,
  disabled = false,
  className = '',
  ...props
}) {
  const labelId = label && id ? `${id}-label` : undefined;

  return (
    <span className={['toggle', className].filter(Boolean).join(' ')}>
      <button
        type="button"
        role="switch"
        id={id}
        aria-checked={checked}
        aria-labelledby={labelId}
        aria-label={!labelId ? label : undefined}
        disabled={disabled}
        className="toggle-button"
        onClick={() => onChange?.(!checked)}
        {...props}
      >
        <span className="toggle-track" aria-hidden="true">
          <span className="toggle-thumb" />
        </span>
      </button>
      {label && (
        <span className="toggle-label" id={labelId}>
          {label}
        </span>
      )}
    </span>
  );
}
